import {
  Mail,
  MessageSquare,
  FileText,
  Users,
  ShoppingCart,
  Calendar,
  Bot,
  GitBranch,
  Zap,
  CheckCircle,
  ArrowRight,
} from "lucide-react"
import { Button } from "@/components/ui/button"

interface WorkflowExample {
  title: string
  description: string
  category: string
  categoryColor: string
  icon: React.ReactNode
  nodes: {
    icon: React.ReactNode
    label: string
    color: string
  }[]
  metrics?: {
    label: string
    value: string
  }
}

const examples: WorkflowExample[] = [
  {
    title: "Email Triage & Auto-Response",
    description: "Automatically classify incoming emails by urgency and intent, then route to the right team or send intelligent auto-replies.",
    category: "Customer Support",
    categoryColor: "bg-blue-100 text-blue-700",
    icon: <Mail className="h-5 w-5" />,
    nodes: [
      { icon: <Zap className="h-3 w-3" />, label: "Gmail", color: "bg-yellow-100 text-yellow-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Classify", color: "bg-purple-100 text-purple-600" },
      { icon: <GitBranch className="h-3 w-3" />, label: "Route", color: "bg-blue-100 text-blue-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Reply", color: "bg-green-100 text-green-600" },
    ],
    metrics: { label: "Avg. response time", value: "< 2 min" },
  },
  {
    title: "Lead Qualification Pipeline",
    description: "Score and qualify leads automatically based on company data, engagement history, and AI analysis.",
    category: "Sales",
    categoryColor: "bg-orange-100 text-orange-700",
    icon: <Users className="h-5 w-5" />,
    nodes: [
      { icon: <Zap className="h-3 w-3" />, label: "Webhook", color: "bg-yellow-100 text-yellow-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Enrich", color: "bg-purple-100 text-purple-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Score", color: "bg-purple-100 text-purple-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "CRM", color: "bg-green-100 text-green-600" },
    ],
    metrics: { label: "Leads processed", value: "500+/day" },
  },
  {
    title: "Content Generation Pipeline",
    description: "Generate blog posts, social media content, and marketing copy from briefs using AI with human review.",
    category: "Marketing",
    categoryColor: "bg-pink-100 text-pink-700",
    icon: <FileText className="h-5 w-5" />,
    nodes: [
      { icon: <Zap className="h-3 w-3" />, label: "Form", color: "bg-yellow-100 text-yellow-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Research", color: "bg-purple-100 text-purple-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Write", color: "bg-purple-100 text-purple-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Publish", color: "bg-green-100 text-green-600" },
    ],
    metrics: { label: "Content created", value: "10x faster" },
  },
  {
    title: "Slack Support Bot",
    description: "Answer employee questions instantly by searching knowledge bases and providing AI-powered responses in Slack.",
    category: "IT Operations",
    categoryColor: "bg-cyan-100 text-cyan-700",
    icon: <MessageSquare className="h-5 w-5" />,
    nodes: [
      { icon: <Zap className="h-3 w-3" />, label: "Slack", color: "bg-yellow-100 text-yellow-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Search", color: "bg-purple-100 text-purple-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Answer", color: "bg-purple-100 text-purple-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Reply", color: "bg-green-100 text-green-600" },
    ],
    metrics: { label: "Questions handled", value: "85% auto" },
  },
  {
    title: "Order Processing Automation",
    description: "Process incoming orders, validate inventory, update systems, and notify customers automatically.",
    category: "E-Commerce",
    categoryColor: "bg-emerald-100 text-emerald-700",
    icon: <ShoppingCart className="h-5 w-5" />,
    nodes: [
      { icon: <Zap className="h-3 w-3" />, label: "Shopify", color: "bg-yellow-100 text-yellow-600" },
      { icon: <GitBranch className="h-3 w-3" />, label: "Validate", color: "bg-blue-100 text-blue-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Fulfill", color: "bg-green-100 text-green-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Notify", color: "bg-green-100 text-green-600" },
    ],
    metrics: { label: "Orders/hour", value: "1000+" },
  },
  {
    title: "Meeting Scheduler & Prep",
    description: "Automatically schedule meetings, send calendar invites, and prepare AI-generated briefing docs.",
    category: "Productivity",
    categoryColor: "bg-violet-100 text-violet-700",
    icon: <Calendar className="h-5 w-5" />,
    nodes: [
      { icon: <Zap className="h-3 w-3" />, label: "Email", color: "bg-yellow-100 text-yellow-600" },
      { icon: <Bot className="h-3 w-3" />, label: "Extract", color: "bg-purple-100 text-purple-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Schedule", color: "bg-green-100 text-green-600" },
      { icon: <CheckCircle className="h-3 w-3" />, label: "Brief", color: "bg-green-100 text-green-600" },
    ],
    metrics: { label: "Time saved", value: "3 hrs/week" },
  },
]

/**
 * Examples section showcasing workflow templates and use cases.
 * Displays a grid of workflow cards with node visualizations.
 */
export function ExamplesSection() {
  return (
    <section className="bg-white py-20 lg:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            Workflows for Every Use Case
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            Get inspired by these real-world automation examples. Start from a
            template or build your own from scratch.
          </p>
        </div>

        {/* Examples Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {examples.map((example, i) => (
            <div
              key={i}
              className="group rounded-xl border border-neutral-200 bg-white p-5 transition-all hover:border-primary/30 hover:shadow-lg"
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  {example.icon}
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${example.categoryColor}`}>
                  {example.category}
                </span>
              </div>

              {/* Content */}
              <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                {example.title}
              </h3>
              <p className="mb-4 text-sm text-neutral-600 line-clamp-2">
                {example.description}
              </p>

              {/* Node Flow Visualization */}
              <div className="mb-4 flex items-center gap-1">
                {example.nodes.map((node, j) => (
                  <div key={j} className="flex items-center">
                    <div
                      className={`flex items-center gap-1 rounded px-2 py-1 ${node.color}`}
                    >
                      {node.icon}
                      <span className="text-xs font-medium">{node.label}</span>
                    </div>
                    {j < example.nodes.length - 1 && (
                      <ArrowRight className="mx-0.5 h-3 w-3 text-neutral-300" />
                    )}
                  </div>
                ))}
              </div>

              {/* Metrics & CTA */}
              <div className="flex items-center justify-between border-t border-neutral-100 pt-4">
                {example.metrics && (
                  <div className="text-xs text-neutral-500">
                    <span className="text-neutral-900 font-medium">{example.metrics.value}</span>
                    {" "}{example.metrics.label}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary/80 hover:bg-primary/5 -mr-2"
                >
                  Use Template
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className="mt-10 text-center">
          <Button variant="outline" size="lg">
            View All Templates
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  )
}
